import * as React from 'react';
import { noop, getStrokeDasharrayCanvas, GenericChartComponent, getMouseCanvas } from '@react-financial-charts/core';
import { saveNodeType, isDefined } from 'react-financial-charts';
import { scaleLinear } from 'd3-scale';
import { max } from 'd3-array';
import { HoverTextNearMouse } from '@react-financial-charts/interactive/lib/components/index';
import { ClickableCircle } from '.';
import type { strokeDashTypes } from 'react-financial-charts';
import type { VolumeProfileRenderOptions, VPFRCacheKey } from '@/utils/volumeProfileRenderer';
import {
  VPFRCacheManager,
  drawBackgroundBox,
  drawVolumeProfileBars,
  drawVolumeProfileLines,
  drawVolumeProfileValues,
} from '@/utils/volumeProfileRenderer';
import type { EachTrendLineProps } from './EachTrendLine';
import type { MoreProps } from '@/types/commontypes';
import { getNewXY } from '@/utils/utils';
import { volumeProfileDefaults } from '@/utils/defaultShapePropertyValues';

// Override id-related types to use number instead of string for volume profile shapes
interface EachFixedRangeVolumeProfileProps extends Omit<
  EachTrendLineProps,
  'uuid' | 'onDrag' | 'onSelect' | 'onHover' | 'onDragStart'
> {
  readonly uuid: string;
  readonly onDrag: (e: React.MouseEvent, uuid: string | undefined, moreProps: Record<string, unknown>) => void;
  readonly onSelect?: (e: React.MouseEvent, uuid: string, moreProps: MoreProps) => void;
  readonly onHover?: (uuid: string | null, moreProps?: MoreProps) => void;
  readonly onDragStart?: (uuid: string) => void;
  readonly isTemp?: boolean;
}

interface EachFixedRangeVolumeProfileState {
  anchor?: string;
  hover?: boolean;
  dragging?: boolean;
}

export class EachFixedRangeVolumeProfile extends React.Component<
  EachFixedRangeVolumeProfileProps,
  EachFixedRangeVolumeProfileState
> {
  public static defaultProps = {
    onDrag: noop,
    onEdge1Drag: noop,
    onEdge2Drag: noop,
    onSelect: noop,
    selected: false,
    edgeStroke: '#1e53e6',
    edgeFill: '#FFFFFF',
    edgeStrokeWidth: 2,
    r: 5,
    strokeWidth: 1,
    strokeDasharray: 'Solid',
    hoverText: {
      enable: false,
    },
    appearance: {
      ...volumeProfileDefaults,
    },
  };

  private dragStart!: Record<string, unknown>;
  private saveNodeType: <T>(type: string) => React.Ref<T>;
  private lineInteractive: string;
  private cacheManager = new VPFRCacheManager();

  public constructor(props: EachFixedRangeVolumeProfileProps) {
    super(props);

    this.saveNodeType = saveNodeType.bind(this);
    this.lineInteractive = 'react-financial-charts-pointer-cursor';

    this.state = {
      hover: false,
    };
  }

  public componentWillUnmount() {
    // Clean up global mouseup listener if component unmounts during drag
    window.removeEventListener('mouseup', this.resetDraggingOnMouseUp);
  }

  private isHover = (moreProps: MoreProps) => {
    const { x1Value, y1Value, x2Value, y2Value, appearance, isTemp } = this.props;
    const {
      mouseXY,
      xScale,
      chartConfig: { yScale, height },
    } = moreProps;

    if (!mouseXY) return false;

    // 마우스가 차트 섹션 밖이면 호버 불가 (서브차트 간 이동 시 호버 고착 방지)
    if (mouseXY[1] < 0 || mouseXY[1] > height) return false;

    const [mouseX, mouseY] = mouseXY;

    // Convert shape coordinates to screen coordinates
    const minXValue = Math.min(x1Value, x2Value);
    const maxXValue = Math.max(x1Value, x2Value);
    const minYValue = Math.min(y1Value, y2Value);
    const maxYValue = Math.max(y1Value, y2Value);

    const x1 = xScale(minXValue);
    const x2 = xScale(maxXValue);
    const y1 = yScale(maxYValue); // yScale is inverted
    const y2 = yScale(minYValue);

    // For temporary shape, just check the bounding box
    if (isTemp) {
      return mouseX >= x1 && mouseX <= x2 && mouseY >= y1 && mouseY <= y2;
    }

    // Calculate histogram area
    const boxWidth = x2 - x1;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const histogramWidth = (boxWidth * ((appearance?.width as number | undefined) || 30)) / 100;
    let histogramStartX: number;
    let histogramEndX: number;

    if (appearance?.placement === 'right') {
      histogramStartX = x2 - histogramWidth;
      histogramEndX = x2;
    } else {
      histogramStartX = x1;
      histogramEndX = x1 + histogramWidth;
    }

    // Check if mouse is over histogram area (expanded hit area)
    const histogramPadding = 5;
    if (
      mouseX >= histogramStartX - histogramPadding &&
      mouseX <= histogramEndX + histogramPadding &&
      mouseY >= y1 &&
      mouseY <= y2
    ) {
      return true;
    }

    // Check if mouse is near POC/VAH/VAL lines (use cached data only - no recalculation)
    const lineHitDistance = 5;
    const vpfrResult = this.cacheManager.getCachedOnly();

    if (vpfrResult && appearance?.volumeProfile) {
      // Check POC line
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      if ((appearance.poc as { show?: boolean } | undefined)?.show !== false) {
        const pocY = yScale(vpfrResult.poc);
        if (mouseX >= x1 && mouseX <= x2 && Math.abs(mouseY - pocY) <= lineHitDistance) {
          return true;
        }
      }

      // Check VAH line
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      if ((appearance.vah as { show?: boolean } | undefined)?.show !== false) {
        const vahY = yScale(vpfrResult.vah);
        if (mouseX >= x1 && mouseX <= x2 && Math.abs(mouseY - vahY) <= lineHitDistance) {
          return true;
        }
      }

      // Check VAL line
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      if ((appearance.val as { show?: boolean } | undefined)?.show !== false) {
        const valY = yScale(vpfrResult.val);
        if (mouseX >= x1 && mouseX <= x2 && Math.abs(mouseY - valY) <= lineHitDistance) {
          return true;
        }
      }
    }

    return false;
  };

  public render() {
    const {
      x1Value,
      y1Value,
      x2Value,
      y2Value,
      hoverText,
      selected,
      edgeStroke,
      edgeFill,
      edgeStrokeWidth,
      r,
      locked,
    } = this.props;

    const {
      enable: hoverTextEnabled,
      selectedText: hoverTextSelected,
      text: hoverTextUnselected,
      ...restHoverTextProps
    } = hoverText;

    const { hover, dragging } = this.state;

    // Update cursor based on dragging state
    if (dragging) {
      this.lineInteractive = 'react-financial-charts-grabbing-cursor';
    } else {
      this.lineInteractive = 'react-financial-charts-pointer-cursor';
    }

    // Smaller radius when locked
    const actualR = locked ? r - 2 : r;
    const actualStroke = locked ? '#436fe9' : edgeStroke;
    const { isTemp } = this.props;
    const showConnectingLine = isTemp || dragging;
    return (
      <g>
        {/* Volume Profile Histogram - on mouse canvas (cleared every frame) */}
        {/* Unmount during drag to hide bars; remount triggers redraw with bars */}
        {!dragging && (
          <GenericChartComponent
            canvasDraw={this.drawFixedRangeVolumeProfileCanvas}
            canvasToDraw={getMouseCanvas}
            drawOn={['mousemove', 'pan', 'drag']}
            onMouseDown={this.handleShapeMouseDown}
            isHover={this.isHover}
            onHover={this.handleHover}
            onUnHover={this.handleUnHover}
            interactiveCursorClass={this.lineInteractive}
            selected={selected || hover}
          />
        )}

        {/* Connecting line - on mouse canvas (cleared every frame) */}
        {showConnectingLine && (
          <GenericChartComponent
            canvasDraw={this.drawConnectingLine}
            canvasToDraw={getMouseCanvas}
            drawOn={['mousemove', 'pan', 'drag']}
            isHover={this.isHover}
            onHover={this.handleHover}
          />
        )}

        {/* Control Point 1 (start point) - rendered last so it appears on top */}
        <ClickableCircle
          ref={this.saveNodeType('edge1')}
          show={selected || (hover ?? false) || (locked ?? false)}
          cx={x1Value}
          cy={y1Value}
          r={actualR}
          fillStyle={edgeFill}
          strokeStyle={actualStroke}
          strokeWidth={edgeStrokeWidth}
          interactiveCursorClass="react-financial-charts-move-cursor"
          onDragStart={!locked ? this.handleEdge1DragStart : noop}
          onDrag={!locked ? this.handleEdge1Drag : noop}
          onDragComplete={!locked ? this.handleDragComplete : noop}
        />

        {/* Control Point 2 (end point) - rendered last so it appears on top */}
        <ClickableCircle
          ref={this.saveNodeType('edge2')}
          show={selected || (hover ?? false) || (locked ?? false)}
          cx={x2Value}
          cy={y2Value}
          r={actualR}
          fillStyle={edgeFill}
          strokeStyle={actualStroke}
          strokeWidth={edgeStrokeWidth}
          interactiveCursorClass="react-financial-charts-move-cursor"
          onDragStart={!locked ? this.handleEdge2DragStart : noop}
          onDrag={!locked ? this.handleEdge2Drag : noop}
          onDragComplete={!locked ? this.handleDragComplete : noop}
        />

        {/* Hover Text */}
        <HoverTextNearMouse
          show={hoverTextEnabled && (hover ?? false)}
          {...restHoverTextProps}
          text={selected ? hoverTextSelected : hoverTextUnselected}
        />
      </g>
    );
  }

  private readonly drawFixedRangeVolumeProfileCanvas = (ctx: CanvasRenderingContext2D, moreProps: MoreProps) => {
    const { isTemp } = this.props;
    if (isTemp) return;
    this.drawFixedRangeVolumeProfile(ctx, moreProps);
  };

  private readonly drawConnectingLine = (ctx: CanvasRenderingContext2D, moreProps: MoreProps) => {
    const { x1Value, y1Value, x2Value, y2Value, appearance } = this.props;

    const {
      xScale,
      chartConfig: { yScale },
    } = moreProps;

    const x1 = xScale(x1Value);
    const y1 = yScale(y1Value);
    const x2 = xScale(x2Value);
    const y2 = yScale(y2Value);

    // Get chart boundaries for vertical lines
    const chartHeight = yScale.range();
    const chartTop = Math.min(...chartHeight);
    const chartBottom = Math.max(...chartHeight);

    // Save canvas state
    ctx.save();

    // Draw line style setup
    /* eslint-disable @typescript-eslint/no-unsafe-type-assertion */
    ctx.strokeStyle = (appearance?.strokeStyle as string | undefined) || '#00000086';
    ctx.lineWidth = (appearance?.strokeWidth as number | undefined) || 1;
    ctx.globalAlpha = (appearance?.strokeOpacity as number | undefined) ?? 1;

    // Set line dash style
    const dashStyle = (appearance?.strokeDasharray as strokeDashTypes | undefined) || 'Solid';
    /* eslint-enable @typescript-eslint/no-unsafe-type-assertion */
    ctx.setLineDash(getStrokeDasharrayCanvas(dashStyle));

    // 1. Diagonal connecting line
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();

    // 2. Vertical at first point
    ctx.beginPath();
    ctx.moveTo(x1, chartTop);
    ctx.lineTo(x1, chartBottom);
    ctx.stroke();

    // 3. Vertical at second point
    ctx.beginPath();
    ctx.moveTo(x2, chartTop);
    ctx.lineTo(x2, chartBottom);
    ctx.stroke();

    // Restore canvas state
    ctx.restore();
  };

  private readonly drawFixedRangeVolumeProfile = (ctx: CanvasRenderingContext2D, moreProps: MoreProps) => {
    const { x1Value, x2Value, appearance, isTemp } = this.props;
    if (isTemp) return;

    // Only draw if shape is visible
    if (!appearance?.volumeProfile) {
      return;
    }

    const {
      xScale,
      chartConfig: { yScale },
      fullData,
      xAccessor,
    } = moreProps;

    // Calculate indices from x values
    const minX = Math.min(x1Value, x2Value);
    const maxX = Math.max(x1Value, x2Value);
    const startIndex = fullData?.findIndex(d => xAccessor(d) >= minX) ?? -1;
    const endIndex = fullData?.findIndex(d => xAccessor(d) >= maxX) ?? -1;

    if (startIndex === -1 || endIndex === -1 || startIndex === endIndex) return;

    // Calculate VPFR with caching
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const rows = (appearance.rowSize as number | undefined) || 7;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const valueAreaPercent = (appearance.valueAreaVolume as number | undefined) || 70;
    const cacheKey: VPFRCacheKey = {
      x1Value: minX,
      x2Value: maxX,
      rowSize: rows,
      valueAreaVolume: valueAreaPercent,
      dataLength: fullData?.length || 0,
    };
    const vpfrResult = this.cacheManager.getOrCalculate(
      fullData,
      startIndex,
      endIndex,
      rows,
      valueAreaPercent,
      cacheKey,
    );
    if (!vpfrResult || vpfrResult.totalVolumes.length === 0) return;

    // Box coordinates (for background)
    const boxLeftX = xScale(minX);
    const boxRightX = xScale(maxX);
    const boxWidth = boxRightX - boxLeftX;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const histogramWidth = (boxWidth * ((appearance.width as number | undefined) || 30)) / 100;

    let histogramStartX: number;
    let histogramEndX: number;
    if (appearance.placement === 'right') {
      histogramStartX = boxRightX - histogramWidth;
      histogramEndX = boxRightX;
    } else {
      histogramStartX = boxLeftX;
      histogramEndX = boxLeftX + histogramWidth;
    }

    // Create volume scale
    const maxVolumeValue = max(vpfrResult.totalVolumes) || 1;
    const isRight = appearance.placement === 'right';

    const volumeScale = scaleLinear()
      .domain([0, maxVolumeValue])
      .range(isRight ? [histogramEndX, histogramStartX] : [histogramStartX, histogramEndX]);

    // Create render options for shared utilities
    const renderOptions: VolumeProfileRenderOptions = {
      appearance,
      vpfrResult,
      yScale,
      rows,
      histogramStartX,
      histogramEndX,
      isRightPlacement: isRight,
      lineLeftX: boxLeftX,
      lineRightX: boxRightX,
      boxLeftX,
      boxRightX,
    };

    // Use shared utilities
    drawBackgroundBox(ctx, renderOptions);
    drawVolumeProfileBars(ctx, renderOptions, volumeScale);
    drawVolumeProfileLines(ctx, renderOptions);
    drawVolumeProfileValues(ctx, renderOptions);
  };

  // Safety: ensure dragging is always reset on mouseup
  private readonly resetDraggingOnMouseUp = () => {
    if (this.state.dragging) this.setState({ dragging: false });
  };

  // Drag handlers
  private readonly handleEdge1DragStart = () => {
    this.dragStart = {
      x1Value: this.props.x1Value,
      y1Value: this.props.y1Value,
    };
    if (!this.state.dragging) this.setState({ dragging: true, hover: true });
    // Ensure dragging resets on mouseup even if onDragComplete doesn't fire
    window.addEventListener('mouseup', this.resetDraggingOnMouseUp, { once: true });
  };

  private readonly handleEdge1Drag = (e: React.MouseEvent, moreProps: MoreProps) => {
    const { locked, x2Value, y2Value, onDrag, uuid } = this.props;
    if (locked) return;

    const [x, y] = getNewXY(moreProps);
    const lastCandleX = this.getLastCandleX(moreProps);

    onDrag(e, uuid, {
      x1Value: x,
      y1Value: y,
      x2Value,
      y2Value,
      lastCandleX,
    });
  };

  private readonly handleEdge2DragStart = () => {
    this.dragStart = {
      x2Value: this.props.x2Value,
      y2Value: this.props.y2Value,
    };
    if (!this.state.dragging) this.setState({ dragging: true, hover: true });
    // Ensure dragging resets on mouseup even if onDragComplete doesn't fire
    window.addEventListener('mouseup', this.resetDraggingOnMouseUp, { once: true });
  };

  private readonly handleEdge2Drag = (e: React.MouseEvent, moreProps: MoreProps) => {
    const { locked, x1Value, y1Value, onDrag, uuid } = this.props;
    if (locked) return;

    const [x, y] = getNewXY(moreProps);
    const lastCandleX = this.getLastCandleX(moreProps);

    onDrag(e, uuid, {
      x1Value,
      y1Value,
      x2Value: x,
      y2Value: y,
      lastCandleX,
    });
  };

  private readonly handleDragComplete = (e: React.MouseEvent, moreProps: MoreProps) => {
    // Remove safety listener if still registered (handleDragComplete fired normally)
    window.removeEventListener('mouseup', this.resetDraggingOnMouseUp);
    if (this.state.dragging) this.setState({ dragging: false });
    const { onDragComplete } = this.props;
    if (onDragComplete) {
      onDragComplete(e, moreProps);
    }
  };

  private getLastCandleX(moreProps: MoreProps): number | undefined {
    const { fullData, xAccessor } = moreProps;
    if (!fullData || fullData.length === 0) return undefined;
    return xAccessor(fullData[fullData.length - 1]);
  }

  private readonly handleHover = (_: React.MouseEvent, moreProps: MoreProps) => {
    const { uuid, onHover } = this.props;
    const hovering = moreProps.hovering || false;

    if (this.state.hover !== hovering) {
      this.setState({ hover: hovering });

      if (onHover) {
        onHover(hovering ? (uuid ?? null) : null, moreProps);
      }
    }
  };

  private readonly handleShapeMouseDown = (e: React.MouseEvent, moreProps: MoreProps) => {
    const { onSelect, uuid } = this.props;
    if (onSelect && (moreProps.hovering || false) && isDefined(uuid)) {
      onSelect(e, uuid, moreProps);
    }
  };

  private readonly handleUnHover = (_: React.MouseEvent, moreProps: MoreProps) => {
    const { onHover } = this.props;

    if (this.state.hover !== false) {
      this.setState({ hover: false });
    }

    if (onHover) {
      onHover(null, moreProps);
    }
  };
}
